import { Config } from "@amtana/module";
import { z } from "zod";

// Web push (ADR-001): VAPID (RFC 8292) proves to the push service which
// server a send came from, with a public/private key pair this module
// owns. `DEV_VAPID_PUBLIC_KEY`/`DEV_VAPID_PRIVATE_KEY` are a key pair
// generated once for development and tests; they sign nothing of value
// (every push service they could ever talk to is loopback), so committing
// them is no different from mailsim's own fixed key (D091). A real
// deployment must set its own pair, or boot refuses while the private key
// still equals this default. `allowLoopbackEndpoints` lets a subscription
// point at the simulator (sims/pushsim) in development and tests;
// production is refused the default, the same way every other simulated
// channel's URL already is (D092).

export const DEV_VAPID_PUBLIC_KEY =
  "BC0Mohj_Q4O9iusfmaZzl79UJjrztMBkFiMN_nLw1Ylu47uDkqxKs4M0EbMVSAGURS46zZ50sxP1I37_5jsgVqk";
export const DEV_VAPID_PRIVATE_KEY = "6Xvd9uk6iJzow_19V2r7UOelFblrEfPH6BhBhtTSwLo";

/** `z.coerce.boolean()` would read any non-empty string, "false" included, as `true`; this
 * reads a variable's absence, or anything but the literal string "false", as `true`. */
const booleanDefaultTrue = () =>
  z
    .string()
    .optional()
    .transform((value) => value !== "false");

export const pushConfig = Config.define((c) => ({
  vapidPublicKey: c.env("PUSH_VAPID_PUBLIC_KEY", z.string().min(1).default(DEV_VAPID_PUBLIC_KEY)),
  vapidPrivateKey: c.env("PUSH_VAPID_PRIVATE_KEY", z.string().min(1).default(DEV_VAPID_PRIVATE_KEY), {
    refuseInProduction: (value) =>
      value === DEV_VAPID_PRIVATE_KEY
        ? "is the committed development VAPID key; a push send would sign as every other deployment still using it"
        : undefined,
  }),
  vapidSubject: c.env("PUSH_VAPID_SUBJECT", z.string().min(1).default("mailto:push@amtana.dev")),
  allowLoopbackEndpoints: c.env("PUSH_ALLOW_LOOPBACK_ENDPOINTS", booleanDefaultTrue(), {
    refuseInProduction: (value) =>
      value ? "allows a subscription endpoint on loopback, the push simulator" : undefined,
  }),
}));
